import { Flex, Heading, Text, Button, Icon, Spinner } from "@chakra-ui/react";
import { WarningIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import OrdersService from "../../api/services/OrdersService";

const PaymentErrorPage = () => {
    const { t } = useTranslation("default");
    const navigate = useNavigate();
    const { id } = useParams();
    const [checking, setChecking] = useState(true);
    const [actuallyPaid, setActuallyPaid] = useState(false);

    useEffect(() => {
        if (!id) {
            setChecking(false);
            return;
        }

        // Sprawdź rzeczywisty status zamówienia z API
        // TPay może przekierować na error nawet gdy płatność przeszła
        let interval = null;

        const checkStatus = async () => {
            try {
                let retryCount = 0;
                const maxRetries = 8;

                const performCheck = async () => {
                    const data = await OrdersService.getStatus(id);
                    if (data.status === "Paid") {
                        setActuallyPaid(true);
                        setChecking(false);
                        return true;
                    }
                    return false;
                };

                const isPaid = await performCheck();

                if (!isPaid) {
                    interval = setInterval(async () => {
                        retryCount++;
                        const finished = await performCheck();
                        if (finished || retryCount >= maxRetries) {
                            clearInterval(interval);
                            interval = null;
                            setChecking(false);
                        }
                    }, 3000);
                }
            } catch (err) {
                console.error("Could not verify order status", err);
                setChecking(false);
            }
        };

        checkStatus();

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [id]);

    if (checking) {
        return (
            <Flex
                flexDirection="column"
                w="100%"
                alignItems="center"
                justifyContent="center"
                bg="white"
                py={{ base: "60px", md: "100px" }}
                px={{ base: "16px", md: "32px" }}
                minH="60vh"
            >
                <Spinner size="xl" color="#7A1F35" thickness="4px" />
                <Text mt={6} fontSize="lg" color="#6E6064" fontFamily="body">
                    {t("payment.checking_status")}
                </Text>
            </Flex>
        );
    }

    // Jeśli zamówienie jest faktycznie opłacone - pokaż sukces
    if (actuallyPaid) {
        return (
            <Flex
                flexDirection="column"
                w="100%"
                alignItems="center"
                justifyContent="center"
                bg="white"
                py={{ base: "60px", md: "100px" }}
                px={{ base: "16px", md: "32px" }}
                minH="60vh"
            >
                <Flex
                    flexDirection="column"
                    alignItems="center"
                    maxW="600px"
                    textAlign="center"
                    gap={6}
                >
                    <Icon as={CheckCircleIcon} w={24} h={24} color="#7A1F35" />
                    <Heading as="h1" size="2xl" color="#1E1517" fontFamily="body">
                        {t("payment.success.title")}
                    </Heading>
                    <Text fontSize="lg" color="#6E6064" fontFamily="body" maxW="400px">
                        {t("payment.success.description")}
                    </Text>

                    <Button
                        mt={4}
                        bg="#7A1F35"
                        color="white"
                        px={8}
                        py={6}
                        borderRadius="full"
                        fontFamily="body"
                        fontWeight="bold"
                        _hover={{ bg: "#5C1628" }}
                        onClick={() => navigate("/")}
                    >
                        {t("payment.return_home")}
                    </Button>
                </Flex>
            </Flex>
        );
    }

    // Rzeczywiście nieopłacone - pokaż error
    return (
        <Flex
            flexDirection="column"
            w="100%"
            alignItems="center"
            justifyContent="center"
            bg="white"
            py={{ base: "60px", md: "100px" }}
            px={{ base: "16px", md: "32px" }}
            minH="60vh"
        >
            <Flex
                flexDirection="column"
                alignItems="center"
                maxW="600px"
                textAlign="center"
                gap={6}
            >
                <Icon as={WarningIcon} w={24} h={24} color="#E53E3E" />
                <Heading as="h1" size="2xl" color="#1E1517" fontFamily="body">
                    {t("payment.error.title")}
                </Heading>
                <Text fontSize="lg" color="#6E6064" fontFamily="body" maxW="400px">
                    {t("payment.error.description")}
                </Text>

                <Button
                    mt={4}
                    bg="#1E1517"
                    color="white"
                    px={8}
                    py={6}
                    borderRadius="full"
                    fontFamily="body"
                    fontWeight="bold"
                    _hover={{ bg: "#1E1517" }}
                    onClick={() => navigate("/")}
                >
                    {t("payment.return_home")}
                </Button>
            </Flex>
        </Flex>
    );
};

export default PaymentErrorPage;
